import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supplierApi, supplierEvalApi } from "../../api";
import { ProcError, ProcForm, ProcLoading, ProcSection } from "../../components/ProcurementLayout";
import { useAsyncData, useMutation } from "../../hooks";
import type { SupplierEvalRecommendation } from "../../types";

const CRITERIA = [
  { key: "deliversOnTime", label: "1. Delivers on Time" },
  { key: "conditionOnArrival", label: "2. Condition on Arrival" },
  { key: "followsInstructions", label: "3. Follows Instructions" },
  { key: "numberOfRejections", label: "4. Number of Rejections" },
  { key: "handlesRejections", label: "5. Handles Rejections" },
  { key: "handlingComplaints", label: "6. Handling of Complaints" },
  { key: "technicalAssistance", label: "7. Technical Assistance" },
  { key: "emergencyAid", label: "8. Emergency Aid" },
  { key: "furnishesInfo", label: "9. Furnishes Information" },
  { key: "deliversWithoutFollowup", label: "10. Delivers Without Follow-up" },
  { key: "reliabilityRecord", label: "11. Reliability Record" },
  { key: "technicalAbility", label: "12. Technical Ability" },
  { key: "overallRatingAgency", label: "13. Overall Rating (Agency)" },
  { key: "overallRatingPurchasing", label: "14. Overall Rating (Purchasing)" },
] as const;

type CriterionKey = typeof CRITERIA[number]["key"];

const RATING_LABELS: Record<number, string> = { 1: "Poor", 2: "Fair", 3: "Good", 4: "Excellent" };

export default function SupplierEvalCreatePage() {
  const navigate = useNavigate();

  const { data: suppliers, loading: suppliersLoading } = useAsyncData(
    () => supplierApi.options(),
    [],
  );

  const [supplierId, setSupplierId] = useState<number | "">("");
  const [evaluationPeriod, setEvaluationPeriod] = useState("");
  const [ratings, setRatings] = useState<Record<CriterionKey, number>>({
    deliversOnTime: 1,
    conditionOnArrival: 1,
    followsInstructions: 1,
    numberOfRejections: 1,
    handlesRejections: 1,
    handlingComplaints: 1,
    technicalAssistance: 1,
    emergencyAid: 1,
    furnishesInfo: 1,
    deliversWithoutFollowup: 1,
    reliabilityRecord: 1,
    technicalAbility: 1,
    overallRatingAgency: 1,
    overallRatingPurchasing: 1,
  });
  const [recommendation, setRecommendation] = useState<SupplierEvalRecommendation>("APPROVED");
  const [comments, setComments] = useState("");

  const totalScore = Object.values(ratings).reduce((s, v) => s + v, 0);
  const percentage = Math.round((totalScore / 56) * 100);
  const scoreColor = percentage >= 80 ? "text-emerald-700" : percentage >= 60 ? "text-amber-600" : "text-red-600";

  const { mutate, loading, error } = useMutation(async () => {
    if (!supplierId) throw new Error("Supplier is required.");
    if (!evaluationPeriod.trim()) throw new Error("Evaluation period is required.");
    const selectedSupplier = suppliers?.find((s) => s.id === supplierId);
    const result = await supplierEvalApi.create({
      supplierId: supplierId as number,
      supplierName: selectedSupplier?.name ?? "",
      evaluationPeriod,
      ...ratings,
      recommendation,
      comments: comments || null,
    });
    navigate(`/procurement/supplier-eval/${result.id}`);
  });

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/procurement/supplier-eval" className="text-sm text-emerald-700 hover:underline">&larr; Back to Supplier Evaluations</Link>
      </div>

      <ProcForm formNo="PROC-FRM-08" title="Supplier Performance Evaluation">
        {suppliersLoading && <ProcLoading label="Loading suppliers..." />}
        {error && <ProcError message={error} />}

        <ProcSection title="Evaluation Details">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Supplier *</label>
              <select value={supplierId} onChange={(e) => setSupplierId(e.target.value ? Number(e.target.value) : "")} className="w-full rounded-xl border px-3 py-2 text-sm" required>
                <option value="">Select supplier...</option>
                {(suppliers ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}{s.email ? ` — ${s.email}` : ""}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Evaluation Period *</label>
              <input type="text" value={evaluationPeriod} onChange={(e) => setEvaluationPeriod(e.target.value)} placeholder="Q1 2026 or Jan–Mar 2026" className="w-full rounded-xl border px-3 py-2 text-sm" required />
            </div>
          </div>
        </ProcSection>

        <ProcSection title="Performance Ratings">
          <p className="text-xs text-slate-500 mb-3">Rate each criterion: 1 = Poor, 2 = Fair, 3 = Good, 4 = Excellent</p>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Criterion</th>
                <th className="px-3 py-2 text-right w-40">Rating (1–4)</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {CRITERIA.map(({ key, label }) => (
                <tr key={key}>
                  <td className="px-3 py-2 text-slate-700">{label}</td>
                  <td className="px-3 py-2">
                    <select
                      value={ratings[key]}
                      onChange={(e) => setRatings((prev) => ({ ...prev, [key]: Number(e.target.value) }))}
                      className="w-full rounded border px-2 py-1 text-sm text-right"
                    >
                      {[1, 2, 3, 4].map((v) => <option key={v} value={v}>{v} — {RATING_LABELS[v]}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 font-bold">
                <td className="px-3 py-2">Total</td>
                <td className={`px-3 py-2 text-right ${scoreColor}`}>{totalScore} / 56 ({percentage}%)</td>
              </tr>
            </tfoot>
          </table>
        </ProcSection>

        <ProcSection title="Recommendation">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Recommendation *</label>
              <select value={recommendation} onChange={(e) => setRecommendation(e.target.value as SupplierEvalRecommendation)} className="w-full rounded-xl border px-3 py-2 text-sm">
                <option value="APPROVED">APPROVED</option>
                <option value="APPROVED_WITH_CONDITIONS">APPROVED WITH CONDITIONS</option>
                <option value="UNDER_OBSERVATION">UNDER OBSERVATION</option>
                <option value="DELISTED">DELISTED</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Comments</label>
              <textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={3} className="w-full rounded-xl border px-3 py-2 text-sm" />
            </div>
          </div>
        </ProcSection>

        <div className="flex justify-end gap-3 pt-2">
          <Link to="/procurement/supplier-eval" className="rounded-xl border px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</Link>
          <button type="button" onClick={() => mutate()} disabled={loading} className="rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60">
            {loading ? "Saving…" : "Save Evaluation"}
          </button>
        </div>
      </ProcForm>
    </div>
  );
}
